import { type AxiosInstance } from "axios";

export interface RealEstateDocumentFetcherResponse {
  requestId: string;
}

export interface RealEstateDocumentFetcherRequest {
  address: string;
  /** マンション名（任意・最大255文字）。空なら送らない。 */
  buildingName?: string;
  areaType: "tokyo_metro" | "nationwide";
  selectedDocuments: string[];
  otherDocuments?: string[];
  comment?: string;
  chibanList: Array<{
    prefName: string;
    location: string;
    chibanAndKaokuNumber: string;
  }>;
  kaokuNumberList?: Array<{
    prefName: string;
    location: string;
    chibanAndKaokuNumber: string;
  }>;
  sharedOwnerNames?: Record<string, string>;
  documentLocations?: Record<string, string>;
  hearingItems?: string[];
  needsPostalDelivery?: boolean;
  postalAddress?: string;
  /** 画面に表示した概算の請求額（円）。納品時に確定額へ更新される暫定値として保存される。 */
  estimatedAmount?: number;
  /** 概算のうち取得手数料の部分（円・税抜）。消費税の計算に使う。 */
  estimatedHandlingFee?: number;
  /** 依頼ごとに発行するキー。応答を受け取れず再送しても、依頼を重複して作らない。 */
  idempotencyKey?: string;
  relatedFiles?: File[];
}

export class RealEstateDocumentFetcherAPI {
  constructor(private readonly apiClient: AxiosInstance) {}

  async submitRequest(
    payload: RealEstateDocumentFetcherRequest
  ): Promise<RealEstateDocumentFetcherResponse> {
    const hasFiles = (payload.relatedFiles?.length ?? 0) > 0;

    const response = hasFiles
      ? await this.apiClient.post<RealEstateDocumentFetcherResponse>(
          "real-estate-document-fetch/",
          this.buildFormData(payload),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      : await this.apiClient.post<RealEstateDocumentFetcherResponse>(
          "real-estate-document-fetch/",
          this.buildJsonPayload(payload)
        );
    return response.data;
  }

  private toSnakeCase(
    payload: RealEstateDocumentFetcherRequest
  ): Record<string, unknown> {
    const convertChiban = (
      list: typeof payload.chibanList
    ): Array<{
      pref_name: string;
      location: string;
      chiban_and_kaoku_number: string;
    }> =>
      list.map((item) => ({
        pref_name: item.prefName,
        location: item.location,
        chiban_and_kaoku_number: item.chibanAndKaokuNumber,
      }));
    return {
      address: payload.address,
      area_type: payload.areaType,
      selected_documents: payload.selectedDocuments,
      chiban_list: convertChiban(payload.chibanList),
      kaoku_number_list: convertChiban(payload.kaokuNumberList ?? []),
      other_documents: payload.otherDocuments ?? [],
      shared_owner_names: payload.sharedOwnerNames ?? {},
      document_locations: payload.documentLocations ?? {},
      hearing_items: payload.hearingItems ?? [],
      needs_postal_delivery: payload.needsPostalDelivery ?? false,
      postal_address: payload.postalAddress ?? "",
      comment: payload.comment ?? "",
      ...(payload.buildingName !== undefined && {
        building_name: payload.buildingName,
      }),
      ...(payload.estimatedAmount !== undefined && {
        estimated_amount: payload.estimatedAmount,
      }),
      ...(payload.estimatedHandlingFee !== undefined && {
        estimated_handling_fee: payload.estimatedHandlingFee,
      }),
      ...(payload.idempotencyKey !== undefined && {
        idempotency_key: payload.idempotencyKey,
      }),
    };
  }

  private buildJsonPayload(payload: RealEstateDocumentFetcherRequest): object {
    return this.toSnakeCase(payload);
  }

  private buildFormData(payload: RealEstateDocumentFetcherRequest): FormData {
    const formData = new FormData();
    const snake = this.toSnakeCase(payload);

    for (const [key, value] of Object.entries(snake)) {
      formData.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : String(value)
      );
    }
    payload.relatedFiles?.forEach((file) => {
      formData.append("related_files", file);
    });

    return formData;
  }
}
